import store from "../store"
import { i18n } from "@/setups/i18n.js"
import { isCookieModeActive, isCookieSessionActive, cookieSessionExpired } from "@/modules/auth/cookieMode.js"

function hasWebLocks() {
	return typeof navigator !== "undefined" && typeof navigator.locks?.request === "function"
}

// Without Web Locks, every tab sends the same rotating refresh token with no cross-tab
// coordination at all (see the unlocked fallback in refreshAccessToken() below) - the auth server
// accepts exactly one submission and rejects the rest, so a "failed" refresh here can simply be
// the losing half of a race a sibling tab's identical attempt is about to WIN. Treating that loss
// as a genuine, unrecoverable failure and calling logOut() immediately would wipe the shared
// storage out from under the sibling's still-in-flight, otherwise-valid success (whose own
// token-identity check in performRefresh() would then find its expected pair gone and discard
// it too). Poll briefly for the sibling's rotation to land before giving up - bounded, so a
// genuine failure (no sibling, or the sibling also failed) still reaches logOut() within a short,
// predictable window instead of hanging.
const STALE_RETRY_INTERVAL_MS = 200
const STALE_RETRY_ATTEMPTS = 8
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

function isExpiredTokenError(error, sentCredential) {
	if (error?.extensions?.code === "authorization" && error?.message?.includes("invalid or expired token")) return true
	return sentCredential && error?.extensions?.code === "AUTH_NOT_AUTHENTICATED"
}

async function waitForSiblingRefreshToken(refreshTokenKey, tokenBeforeRefresh) {
	for (let attempt = 0; attempt < STALE_RETRY_ATTEMPTS; attempt++) {
		const current = localStorage[refreshTokenKey]
		if (current && current !== tokenBeforeRefresh) return current
		await sleep(STALE_RETRY_INTERVAL_MS)
	}
	const current = localStorage[refreshTokenKey]
	return current && current !== tokenBeforeRefresh ? current : null
}

export function makeAxiosGraphQLFetcher({
	// Required
	url,
	axios,

	// Optional
	timeout = 60000,
	headers = {},

	// What is acceptable status code
	// Everything between 200 and 403 inclusive is ok for CoverGo APIs
	minResponseStatusInclusive = 200,
	maxResponseStatusInclusive = 403,

	// Mostly for development, will show queries, variables and headers in the console
	debug = false,
}) {
	if (!axios) throw new Error("Axios dependency is required as a parameter")

	// Create axios instance for CoverGo GraphQL endpoint
	// Opinionated instance
	const coverGoGraphQL = axios.create({
		method: "post",
		timeout,

		// Default headers needed for GraphQL endpoint
		headers: {
			"Content-Type": "application/json",
			...headers,
		},

		validateStatus: function(status) {
			return status >= minResponseStatusInclusive && status < maxResponseStatusInclusive // everything between 200 and 404 is ok for CoverGo APIs
		},
	})

	let refreshTokenRequest = null
	let refreshTokenRequestIdentity = null

	function refreshSuccessMarkerKey(tenantId, clientId) {
		return `covergo-refresh-success:${tenantId}:${clientId}`
	}

	// Deliberately NOT cryptographic (collision resistance isn't the point - equality-checking a
	// value this module already trusts is) and deliberately one-way: this only ever needs to
	// answer "is this still the same pair", never to recover the pair from the fingerprint.
	// JSON-encoding the pair (rather than joining them with a separator) keeps a literal separator
	// character inside either token from making two different pairs hash identically.
	function fingerprint(token, refreshToken) {
		let hash = 0x811c9dc5
		const value = JSON.stringify([token, refreshToken])
		for (let i = 0; i < value.length; i++) {
			hash ^= value.charCodeAt(i)
			hash = Math.imul(hash, 0x01000193)
		}
		return (hash >>> 0).toString(16)
	}

	// A cross-tab lock waiter can't tell a genuine completed refresh apart from some unrelated flow
	// writing a different, non-empty token pair into the same storage keys first. The marker
	// records only a fingerprint, never the raw pair, so it can't itself leak a usable refresh
	// token if left behind by a logout path that doesn't know about this key.
	function getRefreshSuccessMarker(tenantId, clientId) {
		const raw = localStorage.getItem(refreshSuccessMarkerKey(tenantId, clientId))
		if (!raw) return { count: 0, fingerprint: null }
		try {
			const parsed = JSON.parse(raw)
			if (typeof parsed?.count === "number") return parsed
			return { count: 0, fingerprint: null }
		} catch {
			return { count: 0, fingerprint: null }
		}
	}

	// This marker is advisory (an optimization to skip a redundant network call), never load-bearing
	// for the refresh's own success/failure - so a storage write failure here (e.g. quota exceeded,
	// private browsing) must not turn an otherwise-successful refresh (its tokens are already
	// written by this point) into a reported failure.
	function bumpRefreshSuccessMarker(tenantId, clientId, token, refreshToken) {
		try {
			const key = refreshSuccessMarkerKey(tenantId, clientId)
			const previousCount = getRefreshSuccessMarker(tenantId, clientId).count
			localStorage.setItem(key, JSON.stringify({ count: previousCount + 1, fingerprint: fingerprint(token, refreshToken) }))
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Failed to persist refresh success marker", error)
		}
	}

	/** True when `token`/`refreshToken` are the exact pair `marker` was bumped with. */
	function matchesRefreshSuccessMarker(marker, token, refreshToken) {
		if (!token || !refreshToken) return false
		return marker.fingerprint === fingerprint(token, refreshToken)
	}

	// Takes the refresh token AND tenant/client identity explicitly instead of reading them fresh,
	// so a retry after a marker-mismatch (see refreshAccessToken() below) sends what this attempt
	// actually captured before waiting - not whatever unrelated flow (a host or login switch) may
	// have changed them to in the meantime.
	const performRefresh = (refreshToken, tenantId, clientId, coordinated) => {
		return new Promise(resolve => {
			const query = `
				query refreshToken($refreshToken: String!) {
					refreshToken(tenantId: "${tenantId}", clientId: "${clientId}", refreshToken: $refreshToken) {
						accessToken
						refreshToken
					}
				}
			`

			const variables = { refreshToken }
			// This call runs inside the navigator.locks callback below, bounded by the `timeout`
			// this fetcher was configured with (default 60s) - a stalled endpoint would otherwise
			// hold the cross-tab lock forever and block every other caller's refresh too.
			coverGoGraphQL(typeof url === "function" ? url() : url, { data: { query, variables }, headers })
				.then(response => {
					// A login/tenant switch can happen during this network round-trip, after the
					// lock callback's own identity check gated entry into this function - committing
					// this response now would overwrite the new session's shared tokens with a
					// refresh of the old one.
					if (store.state.tenantId !== tenantId || store.state.clientId !== clientId) {
						resolve(false)
						return
					}

					// A logout+login for a DIFFERENT session under the SAME tenant/client can also
					// happen during this round-trip - tenantId/clientId equality above won't catch
					// that. Only commit this response if the refresh token it's actually for is still
					// the one in storage; otherwise a fresher session's tokens would be clobbered with
					// a refresh of the one it just replaced.
					if (localStorage[process.env.VUE_APP_REFRESH_TOKEN_KEY] !== refreshToken) {
						resolve(false)
						return
					}

					const newAccessToken = response?.data?.data?.refreshToken?.accessToken
					const newRefreshToken = response?.data?.data?.refreshToken?.refreshToken
					// Never store whatever comes back unconditionally: an empty/failed response would
					// literally store the string "undefined" as a "valid-looking" non-empty token,
					// defeating every empty-storage/marker check below. Only ever store an actual pair.
					if (!newAccessToken || !newRefreshToken) {
						// The server just told us this exact refresh token is invalid - leaving it in
						// storage would let a tab queued behind this one's lock acquire it, see the
						// same non-empty (but now known-dead) token, and send the identical doomed
						// request again. Clear it (only if still untouched by anything else) so that
						// waiter's own defensive "no current refresh token" check short-circuits
						// instead. Not applied to the network-error catch below - a transient failure
						// there isn't evidence the token itself is bad.
						// Only when `coordinated` (this call went through the cross-tab lock): in the
						// Web-Locks-unavailable fallback, a same-tab-only dedupe cannot stop a
						// DIFFERENT tab from concurrently sending this exact token too. If that
						// concurrent request is still in flight and wins after this one loses,
						// clearing here would make its own response fail the token-identity check
						// above once it arrives - turning one tab's real failure into two tabs
						// logging out for what was actually a successful refresh. Leaving the token in
						// place costs at most one redundant retry, which is the safer trade-off.
						if (coordinated && localStorage[process.env.VUE_APP_REFRESH_TOKEN_KEY] === refreshToken) {
							localStorage.removeItem(process.env.VUE_APP_REFRESH_TOKEN_KEY)
						}
						resolve(false)
						return
					}
					localStorage.setItem(process.env.VUE_APP_TOKEN_KEY, newAccessToken)
					localStorage.setItem(process.env.VUE_APP_REFRESH_TOKEN_KEY, newRefreshToken)
					// File requests (uploads, downloads, PDF rendering) read the bearer from
					// store.state.auth.token, not localStorage - it's seeded from storage only once,
					// at store construction. Without this, every one of those requests keeps sending
					// the now-rotated-away access token after any refresh, until a full page reload
					// re-seeds it. setAccessToken() is this store module's own existing action for
					// exactly this: keeping the in-memory credential in sync with an external write.
					store.dispatch("auth/setAccessToken", newAccessToken)
					bumpRefreshSuccessMarker(tenantId, clientId, newAccessToken, newRefreshToken)
					resolve(newRefreshToken)
				})
				.catch(error => {
					// Without this handler, a rejected request (a network error, or the configured
					// timeout firing) would leave this promise pending forever, which, combined with
					// the lock, would block every other caller indefinitely instead of just this one
					// failing.
					// Logging `error` itself would leak the live refresh token: Axios errors retain
					// the request config, whose `data` holds the `variables.refreshToken` we just sent.
					// eslint-disable-next-line no-console
					console.error("Axios error during token refresh", error?.message)
					resolve(false)
				})
		})
	}

	const refreshAccessToken = () => {
		const tenantIdBeforeRefresh = store.state.tenantId
		const clientIdBeforeRefresh = store.state.clientId

		// Read the marker snapshot before capturing the token: if it happened the other way round, a
		// refresh completing between the two reads would leave the marker snapshot already
		// reflecting the new state while the captured token is the stale, already-rotated one - so
		// the lock callback below would see no marker change and retry with a token that's already
		// dead.
		const successMarkerBefore = getRefreshSuccessMarker(tenantIdBeforeRefresh, clientIdBeforeRefresh)

		// Captured after the marker snapshot above, but still before the dedupe check below, not
		// just before performRefresh: a same-tenant logout+login can replace the refresh token
		// between one caller starting a refresh and a second caller arriving while it's still
		// pending - tenant/client alone can't tell that second caller it's not the same session
		// being refreshed anymore.
		const tokenBeforeRefresh = localStorage[process.env.VUE_APP_REFRESH_TOKEN_KEY]

		// refreshTokenRequest dedupes concurrent calls within this tab, but only for the SAME
		// tenant/client AND the same captured refresh token: it's a single shared variable, while
		// the lock and success marker below are both keyed by identity. A tenant/client switch, or a
		// same-tenant relogin that replaced the token, mid-flight means the pending promise still
		// belongs to the old identity/token and will resolve false via the lock callback's own
		// checks - reusing it here for a caller whose OWN captured token is already current would
		// misread that unrelated "false" as "my perfectly valid session's refresh failed" and log
		// out a session that never needed refreshing.
		if (
			refreshTokenRequest &&
			refreshTokenRequestIdentity?.tenantId === tenantIdBeforeRefresh &&
			refreshTokenRequestIdentity?.clientId === clientIdBeforeRefresh &&
			refreshTokenRequestIdentity?.refreshToken === tokenBeforeRefresh
		) {
			return refreshTokenRequest
		}

		const hasLocks = hasWebLocks()

		// Every caller that gets a 401 independently calls this, including several in-flight
		// requests in the same tab and any other tab of the same tenant/client - without dedup and
		// a cross-tab lock, all of them would race each other to refresh the same token.
		// Some environments declare `navigator.locks` but leave it null/undefined rather than
		// omitting it entirely, so `"locks" in navigator` alone is not a safe feature check.
		// The locked path below bails out early when there's no refresh token to send (see the
		// defensive check inside the lock callback) - the unlocked fallback needs the same guard,
		// since it skips straight to performRefresh() and would otherwise send a malformed request
		// with an undefined refreshToken variable instead of resolving false like the locked path.
		if (!tokenBeforeRefresh) {
			return Promise.resolve(false)
		}

		refreshTokenRequest = hasLocks
			? navigator.locks.request(`covergo-refresh-token:${store.state.tenantId}:${store.state.clientId}`, () => {
					// A host or login switch while this request was queued means tokenBeforeRefresh
					// no longer belongs to the current tenant/client - sending it under the new
					// identity could refresh the wrong session, or have its result overwrite the
					// new session's shared storage. Checked before anything else below.
					if (store.state.tenantId !== tenantIdBeforeRefresh || store.state.clientId !== clientIdBeforeRefresh) {
						return false
					}

					// Another caller (this tab or another) may have already refreshed while we
					// were waiting for the lock. Comparing token values before/after can't tell
					// a genuine refresh apart from some other, unrelated flow populating a
					// *different* non-empty pair here first. bumpRefreshSuccessMarker() is
					// called ONLY by the actual refresh-success path above, so comparing the
					// marker's count is an unambiguous "did a real refresh happen?" signal - and
					// since a marker bump can still be followed by an unrelated overwrite before
					// this callback runs, the marker also records a fingerprint of the pair the
					// success left behind, so we additionally verify that pair is still in
					// storage before trusting it.
					const successMarker = getRefreshSuccessMarker(tenantIdBeforeRefresh, clientIdBeforeRefresh)
					const currentRefreshToken = localStorage[process.env.VUE_APP_REFRESH_TOKEN_KEY]
					const currentAccessToken = localStorage[process.env.VUE_APP_TOKEN_KEY]
					if (
						successMarker.count !== successMarkerBefore.count &&
						matchesRefreshSuccessMarker(successMarker, currentAccessToken, currentRefreshToken)
					) {
						// This waiter is reusing another tab's already-completed refresh rather than
						// performing its own - performRefresh()'s own sync only runs on the path that
						// actually calls it, so this tab's in-memory auth.token (read by file requests
						// via bearerHeaders()) would otherwise stay on the pre-refresh value indefinitely.
						store.dispatch("auth/setAccessToken", currentAccessToken)
						return currentRefreshToken
					}

					// Defensive: a refresh cannot proceed without a current refresh token - if a
					// previous holder's refresh already failed and cleared it (or logout did),
					// don't bother retrying with nothing to send. Checked on the refresh token
					// specifically, not "both empty": an access token surviving alone still can't
					// be exchanged for anything.
					if (!currentRefreshToken) return false

					// tenantId/clientId equality above doesn't catch a logout+login for a
					// DIFFERENT session under the SAME tenant/client while this request was
					// queued - that clears and rewrites the token pair without changing either
					// id. Sending tokenBeforeRefresh in that case would try to exchange a token
					// that no longer belongs to the now-current session, so only retry when
					// storage still shows the exact pair this attempt started with.
					if (currentRefreshToken !== tokenBeforeRefresh) return false

					return performRefresh(tokenBeforeRefresh, tenantIdBeforeRefresh, clientIdBeforeRefresh, true)
			  })
			: performRefresh(tokenBeforeRefresh, tenantIdBeforeRefresh, clientIdBeforeRefresh, false)
		const thisRequest = refreshTokenRequest.finally(() => {
			// A newer call (a different identity, while this one was still pending) may have
			// already replaced refreshTokenRequest/refreshTokenRequestIdentity with its own -
			// clearing them unconditionally here would wipe out that newer request's still-active
			// state, letting yet another caller start a third, redundant performRefresh in the
			// Web-Locks-unavailable fallback (where this shared variable is the only in-tab dedup).
			// Only clear the state that's still exactly the one this invocation created.
			if (refreshTokenRequest === thisRequest) {
				refreshTokenRequest = null
				refreshTokenRequestIdentity = null
			}
		})
		refreshTokenRequest = thisRequest
		refreshTokenRequestIdentity = {
			tenantId: tenantIdBeforeRefresh,
			clientId: clientIdBeforeRefresh,
			refreshToken: tokenBeforeRefresh,
		}

		return refreshTokenRequest
	}

	const caller = ({
		query = null,
		variables = null,
		locale = store?.getters?.locale || "en-US",
		token = localStorage[process.env.VUE_APP_TOKEN_KEY],
	}) => {
		const headers = {}
		token = store.state.accessToken || localStorage[process.env.VUE_APP_TOKEN_KEY]
		if (locale) headers["Accept-Language"] = locale
		if (token) headers.Authorization = `Bearer ${token}` // append this header only if token exists otherwise can cause weird issues with API

		return new Promise((resolve, reject) => {
			const requestId = Math.random()
				.toString()
				.substr(2, 4)

			const logOut = () => {
				localStorage.removeItem(process.env.VUE_APP_TOKEN_KEY)
				localStorage.removeItem(process.env.VUE_APP_REFRESH_TOKEN_KEY)
				window.location = `/`
			}

			if (debug) {
				/* eslint-disable no-console */
				console.groupCollapsed(`%c CoverGo Request ${requestId}`, "padding: 1px 6px 1px 0px; background: #0256ab; color: white")
				console.log(query)
				console.log(JSON.stringify(variables))
				console.log(variables)
				console.log(JSON.stringify(headers))
				console.groupEnd()
				/* eslint-enable no-console */
			}

			const resolvedUrl = typeof url === "function" ? url() : url // if url is a function, we evaluate, otherwise use directly url string

			const sentCredential = Boolean(headers.Authorization) || isCookieSessionActive()

			coverGoGraphQL(resolvedUrl, { data: { query, variables }, headers })
				.then(async response => {
					if (debug) {
						/* eslint-disable no-console */
						console.groupCollapsed(
							`%c CoverGo Response ${requestId}`,
							"padding: 1px 6px 1px 0px; color: #0256ab; background: white"
						)
						console.log(response)
						console.groupEnd()
						/* eslint-enable no-console */
					}
					// CH-37826: validateStatus accepts 200..402, so a 401 from the same-origin
					// auth_request gate resolves as SUCCESS and would fall through silently.
					// Gated, so the legacy path for every other host is unchanged.
					if (isCookieModeActive() && response?.status === 401) {
						cookieSessionExpired()
						return resolve(null)
					}

					if (response?.data?.errors?.find(error => isExpiredTokenError(error, sentCredential))) {
						// The BFF owns refresh in cookie mode: there is no refresh token in
						// localStorage, and the refreshToken query would 401 against the gate.
						if (isCookieModeActive()) {
							cookieSessionExpired()
							return resolve(null)
						}

						const tenantIdBeforeRefresh = store.state.tenantId
						const clientIdBeforeRefresh = store.state.clientId
						const refreshTokenBeforeRefresh = localStorage[process.env.VUE_APP_REFRESH_TOKEN_KEY]
						const refreshToken = await refreshAccessToken()

						if (!refreshToken) {
							const identityChanged =
								store.state.tenantId !== tenantIdBeforeRefresh || store.state.clientId !== clientIdBeforeRefresh
							let currentRefreshToken = localStorage[process.env.VUE_APP_REFRESH_TOKEN_KEY]
							// A tenant/client switch, or a fresh login for a different session under the
							// SAME tenant/client (a new non-empty refresh token superseding the one this call
							// captured), means this false is stale - the new session may be perfectly valid,
							// and logging out here would wrongly clear its just-written tokens. A refresh
							// token that's now missing/cleared instead (a genuine logout, by this tab or
							// another) is NOT stale: every tab should still end up logged out when that
							// happens, same as a real refresh failure that never touched storage at all.
							let supersededBySameIdentityLogin =
								!identityChanged && currentRefreshToken && currentRefreshToken !== refreshTokenBeforeRefresh

							// Only relevant to the no-Web-Locks fallback (see hasWebLocks() above): under a
							// real lock, only one tab can ever be mid-refresh at a time, so a "false" here
							// is never the losing half of a same-token race - skip the wait there rather
							// than add a pointless delay to a genuine failure.
							if (!identityChanged && !supersededBySameIdentityLogin && !hasWebLocks()) {
								const rotated = await waitForSiblingRefreshToken(process.env.VUE_APP_REFRESH_TOKEN_KEY, refreshTokenBeforeRefresh)
								if (rotated) {
									currentRefreshToken = rotated
									supersededBySameIdentityLogin = true
								}
							}

							if (identityChanged || supersededBySameIdentityLogin) {
								// The retry below reuses whatever session now owns storage - a tenant/client
								// switch or a fresh same-identity login, neither of which this tab's refresh
								// call performed itself, so store.state.auth.token (read by file requests via
								// bearerHeaders()) was never synced to it. Without this, those requests keep
								// sending the pre-switch/pre-login access token until a full page reload.
								const currentAccessToken = localStorage[process.env.VUE_APP_TOKEN_KEY]
								store.dispatch("auth/setAccessToken", currentAccessToken)
								const retryData = await caller({ query, variables })
								return resolve(retryData)
							}
							logOut()
							showError()
							return resolve(null)
						}
						const retryData = await caller({ query, variables })
						resolve(retryData)
					}

					// Axios returns response.data.data, we want consumer to access directly response.data no response.data.data
					resolve(response?.data)
				})
				.catch(error => {
					// Logging `error` itself would leak the live access token: Axios errors retain the
					// request config, whose `headers.Authorization` holds the bearer token we just sent.
					// eslint-disable-next-line no-console
					console.error("Axios error during fetching", error?.message)

					// Show error to users as some modules are not handling this properly so define it on global to avoid weird broken states without error message
					showError()

					// From Axios docs:
					// if (error.response) // The request was made and the server responded with a status code that falls out of the defined range
					// else if (error.request) The request was made but no response was received
					// else Something happened in setting up the request that triggered an Error

					// CoverGo expects fetcherErrorCodeResolver function that provides SERVER or NETWORK error codes
					// When error.status is undefined, it was most likely NETWORK error
					// When error.status is out of CoverGo defined range, it was most likely SERVER error
					// eslint-disable-next-line prefer-promise-reject-errors
					reject({
						fetcherErrorCodeResolver: () =>
							error?.status ? { code: "SERVER", message: error?.message } : { code: "NETWORK", message: error?.message },
					})
				})
		})
	}
	return caller
}

const showError = ({ code = "general", locale = store.getters.locale ?? "en-US" } = {}) => {
	store.dispatch("addToastMessage", {
		type: "error",
		content: {
			type: "message",
			text: i18n.messages[locale].global.errors[code],
		},
	})
}
